import React, {useState, useMemo, createRef, useEffect} from 'react';
import TimeUtils from 'utils/time';
import dayjs from 'utils/dayjs-timezone';
import PropTypes from 'prop-types';
import {Modal, ModalHeader, useToast, Alert} from '@spothero/ui';
import ExperimentUtils, {IMMEDIATE_FEATURES} from 'utils/experiment';
import {toggleModal} from 'store/parking-pass/parking-pass-actions';
import {CHANGE_TIMES} from 'store/parking-pass/parking-pass-reducer';
import {useDispatch, useSelector} from 'react-redux';
import ChangeTimesSelect from '../change-times-select';
import ChangeTimesPayment from '../change-times-payment';
import ChangeTimesRefund from '../change-times-refund';
import ChangeTimesNoCharge from '../change-times-no-charge';
import {PROPTYPE_RESERVATION} from 'proptypes/reservation-prop-types';
import trackReservationUpdated, {
    LOCATIONS,
    UPDATED_FIELDS,
} from 'segment/events/reservation-updated';
import usePrevious from 'hooks/use-previous';
import trackReservationTimesAdjusted from 'segment/events/reservation-times-adjusted';
import trackReservationTimesAdjustmentFailed from 'segment/events/reservation-times-adjustment-failed';

// Views
const TIME_SELECT_VIEW = 'TIME_SELECT_VIEW';
const PAYMENT_VIEW = 'PAYMENT_VIEW';
const REFUND_VIEW = 'REFUND_VIEW';
const NO_PAYMENT_VIEW = 'NO_PAYMENT_VIEW';

const ChangeTimesModal = ({canEditTimes, reservation, activeStatus}) => {
    const dispatch = useDispatch();
    const user = useSelector(state => state.user.data);
    const [currentView, setCurrentView] = useState(TIME_SELECT_VIEW);
    const [rateDiff, setRateDiff] = useState(null);
    const [starts, setStarts] = useState(
        reservation.starts.format(TimeUtils.V1_API_TIMESTAMP_FORMAT)
    );
    const [ends, setEnds] = useState(
        reservation.ends.format(TimeUtils.V1_API_TIMESTAMP_FORMAT)
    );
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useToast();
    const addToast = () => {
        const toastRef = createRef();

        const close = () => {
            if (toastRef.current) {
                toast.close(toastRef.current);
            }
        };

        toastRef.current = toast({
            position: 'top',
            duration: 3000,
            render: () => (
                <Alert
                    title="Reservation times updated"
                    status="success"
                    showIcon
                    showDismiss
                    onDismissClick={close}
                />
            ),
        });
    };

    const prevReservationStarts = usePrevious(reservation.starts);
    const prevReservationEnds = usePrevious(reservation.ends);

    // If reservation updates - want to update the starts and ends here
    useEffect(() => {
        const startsChanged =
            reservation.starts &&
            prevReservationStarts &&
            !reservation.starts.isSame(prevReservationStarts);
        const endsChanged =
            reservation.ends &&
            prevReservationEnds &&
            !reservation.ends.isSame(prevReservationEnds);

        if (startsChanged || endsChanged) {
            setStarts(
                reservation.starts.format(TimeUtils.V1_API_TIMESTAMP_FORMAT)
            );
            setEnds(reservation.ends.format(TimeUtils.V1_API_TIMESTAMP_FORMAT));
        }
    }, [
        prevReservationStarts,
        prevReservationEnds,
        reservation.starts,
        reservation.ends,
    ]);

    const handleClose = () => {
        dispatch(toggleModal({modal: CHANGE_TIMES}));
    };

    const updateStartsAndEnds = ({starts: newStarts, ends: newEnds}) => {
        setRateDiff(null);
        setStarts(newStarts);
        setEnds(newEnds);
    };

    const resetModalState = () => {
        setCurrentView(TIME_SELECT_VIEW);
        setRateDiff(null);
        setStarts(reservation.starts.format(TimeUtils.V1_API_TIMESTAMP_FORMAT));
        setEnds(reservation.ends.format(TimeUtils.V1_API_TIMESTAMP_FORMAT));
    };

    const handleRateDiff = newRateDiff => {
        setRateDiff(newRateDiff);
        if (newRateDiff.priceDifference < 0) {
            setCurrentView(REFUND_VIEW);
        } else if (newRateDiff.priceDifference > 0) {
            setCurrentView(PAYMENT_VIEW);
        } else {
            setCurrentView(NO_PAYMENT_VIEW);
        }
    };

    const handleSuccess = ({refundAsCredit} = {}) => {
        setIsSubmitting(false);

        const originalDuration = dayjs(reservation.ends).diff(
            reservation.starts,
            'minute'
        );
        const newDuration = dayjs(rateDiff.ends).diff(
            rateDiff.starts,
            'minute'
        );

        trackReservationTimesAdjusted({
            reservation,
            rateDiff,
            reservationPriceChanged: rateDiff.priceDifference,
            activeStatus,
            refundAsCredit,
            user,
        });

        trackReservationUpdated({
            reservation,
            updatedProperties: [UPDATED_FIELDS.TIMES],
            location: LOCATIONS.PARKING_PASS,
            reservationPriceChanged: rateDiff.priceDifference,
            activeStatus,
            user,
            extraTimePurchased: newDuration - originalDuration,
        });

        handleClose();
        addToast();
    };

    const handleError = errorMessage => {
        setIsSubmitting(false);

        trackReservationTimesAdjustmentFailed({
            reservation,
            rateDiff,
            reservationPriceChanged: rateDiff.priceDifference,
            activeStatus,
            user,
            errorMessage,
        });
    };

    const handleBackToTimeSelect = () => {
        setCurrentView(TIME_SELECT_VIEW);
        setRateDiff(null);
    };

    const isAutoExtension = useMemo(() => {
        if (!rateDiff) {
            return;
        }

        const rateEndsObj = dayjs(rateDiff.ends).tz(reservation.timezone);
        const endObj = dayjs.tz(ends, reservation.timezone);
        const autoExtensionEndDifference = rateEndsObj.diff(endObj, 'minute');

        if (autoExtensionEndDifference > 0) {
            return true;
        }

        return false;
    }, [rateDiff, ends, reservation.timezone]);

    const showModal = useSelector(
        store => store.parkingPass.modals[CHANGE_TIMES]
    );

    const userInExperiment = ExperimentUtils.isFeatureEnabled(
        IMMEDIATE_FEATURES.PARKING_PASS_TIME_ADJUSTMENTS
    );

    if (!canEditTimes || !userInExperiment) {
        return null;
    }

    return (
        <Modal
            hasHeader
            hasFooter
            size="sm"
            contentStyling={{
                maxHeight: {
                    base: '100%',
                    tablet: '65%',
                },
                minWidth: {
                    base: '100%',
                    tablet: 96,
                },
            }}
            isOpen={showModal}
            onClose={handleClose}
            onCloseComplete={resetModalState}
            closeOnOverlayClick={false}
            data-testid="ChangeTimesModal"
            hideCloseButton={isSubmitting}
        >
            <ModalHeader fontSize="lg">Change reservation time</ModalHeader>
            {currentView === TIME_SELECT_VIEW && (
                <ChangeTimesSelect
                    onCloseModal={handleClose}
                    updateStartsAndEnds={updateStartsAndEnds}
                    initialStarts={starts}
                    initialEnds={ends}
                    reservation={reservation}
                    handleRateDiff={handleRateDiff}
                />
            )}
            {currentView === PAYMENT_VIEW && (
                <ChangeTimesPayment
                    handleBackToTimeSelect={handleBackToTimeSelect}
                    onCloseModal={handleClose}
                    rateDiff={rateDiff}
                    timezone={reservation.timezone}
                    isAutoExtension={isAutoExtension}
                    isSubmitting={isSubmitting}
                    reservation={reservation}
                    onSuccess={handleSuccess}
                    setIsSubmitting={setIsSubmitting}
                    onError={handleError}
                />
            )}
            {currentView === REFUND_VIEW && (
                <ChangeTimesRefund
                    handleBackToTimeSelect={handleBackToTimeSelect}
                    onCloseModal={handleClose}
                    rateDiff={rateDiff}
                    timezone={reservation.timezone}
                    isAutoExtension={isAutoExtension}
                    reservation={reservation}
                    setIsSubmitting={setIsSubmitting}
                    isSubmitting={isSubmitting}
                    onSuccess={handleSuccess}
                    onError={handleError}
                />
            )}
            {currentView === NO_PAYMENT_VIEW && (
                <ChangeTimesNoCharge
                    handleBackToTimeSelect={handleBackToTimeSelect}
                    onCloseModal={handleClose}
                    rateDiff={rateDiff}
                    timezone={reservation.timezone}
                    isAutoExtension={isAutoExtension}
                    reservation={reservation}
                    onSuccess={handleSuccess}
                    isSubmitting={isSubmitting}
                    setIsSubmitting={setIsSubmitting}
                    onError={handleError}
                />
            )}
        </Modal>
    );
};

ChangeTimesModal.propTypes = {
    reservation: PROPTYPE_RESERVATION,
    canEditTimes: PropTypes.bool.isRequired,
    activeStatus: PropTypes.string.isRequired,
};

export default ChangeTimesModal;
